import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CubeWorkflowData,
  GraphNode,
  WriteDataFrameTransitFormGroup,
  WriteDataFrameTransitParameters,
  WriteDataFrameTransitRawParameters,
  WriteDataFrameTransitSaveMode,
} from '@selfai-platform/pipeline-common';
import { FormFieldComponentModule } from '@selfai-platform/pipeline-shared';
import { DialogService } from '@selfai-platform/shell';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { MessageModule } from 'primeng/message';
import { WorkflowEditorFacadeService } from '../../../../workflow-editor';
import { CubeDialogManagementService } from '../../../services/cube-dialog-management.service';
import { DataSourceSelectorComponent } from '../../data-source-selector';
import { DialogHeaderService } from '../../dialog-header/dialog-header.service';

@Component({
  selector: 'selfai-platform-write-data-frame',
  templateUrl: './write-data-frame.component.html',
  styleUrl: './write-data-frame.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormFieldComponentModule,
    DataSourceSelectorComponent,
    CheckboxModule,
    ReactiveFormsModule,
    TranslateModule,
    MessageModule,
    DropdownModule,
  ],
  providers: [DialogHeaderService],
})
export class WriteDataFrameComponent implements OnInit {
  get node(): GraphNode<WriteDataFrameTransitRawParameters> | undefined {
    return this.dialogService?.data?.selectedNode;
  }

  get nodeId(): string {
    return this.node.id;
  }

  get hasParameters(): boolean {
    return Boolean(this.node.parameters);
  }

  get nodeParameters(): WriteDataFrameTransitParameters {
    return this.normalizeRawWriteDataFrameTransitParameters(this.node.parameters?.serialize());
  }

  form = new FormGroup<WriteDataFrameTransitFormGroup>({
    dataSourceId: new FormControl(null, [Validators.required]),
    overwrite: new FormControl(null),
    saveMode: new FormControl(null),
  });

  saveModeOptions: WriteDataFrameTransitSaveMode[] = Object.values(WriteDataFrameTransitSaveMode);

  constructor(
    private readonly workflowEditorFacadeService: WorkflowEditorFacadeService,
    private readonly dialogService: DialogService<undefined, CubeWorkflowData<WriteDataFrameTransitRawParameters>>,
    private readonly dialogHeaderService: DialogHeaderService,
    private readonly cubeDialogManagementService: CubeDialogManagementService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.form.patchValue(this.nodeParameters);
    this.dialogHeaderService.initCustomHeaderComponent(
      this.nodeId,
      this.translateService.instant('workflow.cubes.write-data-frame.modal-header'),
      this.onCloseDialog.bind(this),
      this.onSubmit.bind(this),
    );
    setTimeout(() => {
      this.cubeDialogManagementService.setFocus(this.nodeId);
    });
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.workflowEditorFacadeService.updateNodeParamterValues({
        id: this.nodeId,
        parameters: this.normalizeFormValuesToApiModel(this.form.value as WriteDataFrameTransitParameters),
      });
      this.dialogService.close();
    }
  }

  onCloseDialog(): void {
    this.dialogService.close();
  }

  private normalizeFormValuesToApiModel(params: WriteDataFrameTransitParameters): WriteDataFrameTransitRawParameters {
    return {
      'data source': params.dataSourceId,
      overwrite: params.overwrite,
      'Save mode (only for JDBC)': params.saveMode ? { [params.saveMode]: {} } : null,
    };
  }

  private normalizeRawWriteDataFrameTransitParameters(
    rawParameters: WriteDataFrameTransitRawParameters,
  ): WriteDataFrameTransitParameters {
    return {
      dataSourceId: rawParameters['data source'],
      overwrite: rawParameters.overwrite,
      saveMode: rawParameters['Save mode (only for JDBC)']
        ? (Object.keys(rawParameters['Save mode (only for JDBC)'])[0] as WriteDataFrameTransitSaveMode)
        : null,
    };
  }
}
